import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mindful-halotherapy/artifacts/6y9td2wh_logo%20marron.png";
const LOGO_WHITE = "https://customer-assets.emergentagent.com/job_mindful-halotherapy/artifacts/b6ilxmet_logo%20ocmpleto.png";

const navLinks = [
  { name: 'Inicio', path: '/' },
  { name: 'Haloterapia', path: '/haloterapia' },
  { name: 'Servicios', path: '/servicios' },
  { name: 'Beneficios', path: '/beneficios' },
  { name: 'Familias', path: '/familias' },
  { name: 'Filosofía', path: '/filosofia' },
  { name: 'Reservar', path: '/reservar' },
  { name: 'Bonos', path: '/bonos' },
  { name: 'Contacto', path: '/contacto' },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <>
      {/* Top bar - Hidden on mobile */}
      <div className="bg-halo-charcoal text-halo-cream py-2 px-4 text-xs md:text-sm hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href="tel:678901086" className="flex items-center gap-2 hover:text-halo-brown transition-colors">
              <Phone size={14} />
              <span>678 901 086</span>
            </a>
            <span className="flex items-center gap-2">
              <MapPin size={14} />
              <span>C/ Miquel Caplloch 6, Local 12</span>
            </span>
          </div>
          <Link 
            to="/bonos" 
            className="hover:text-halo-brown transition-colors font-medium"
            data-testid="top-bar-reservas"
          >
            Comprar bono
          </Link>
        </div>
      </div>

      {/* Main navigation */}
      <nav 
        className={`sticky top-0 z-50 transition-all duration-500 ${
          isScrolled 
            ? 'glass-nav shadow-lg' 
            : 'bg-halo-charcoal md:bg-halo-cream/95 md:backdrop-blur-sm'
        }`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo - White on mobile, colored on desktop */}
            <Link to="/" className="flex-shrink-0" data-testid="logo-link">
              {/* Mobile logo (white/inverted) */}
              <img 
                src={LOGO_URL} 
                alt="HALO Cuevas de Sal" 
                className="h-12 w-auto object-contain md:hidden brightness-0 invert"
                style={{ maxWidth: '120px' }}
              />
              {/* Desktop logo (original color) */}
              <img 
                src={LOGO_URL} 
                alt="HALO Cuevas de Sal" 
                className="hidden md:block h-14 w-auto object-contain"
                style={{ maxWidth: '120px' }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-0.5 xl:gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-2 xl:px-3 py-2 text-xs xl:text-sm font-body font-medium transition-all duration-300 rounded-full hover:bg-halo-brown/10 whitespace-nowrap ${
                    location.pathname === link.path 
                      ? 'text-halo-brown' 
                      : 'text-halo-charcoal hover:text-halo-brown'
                  }`}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <Link
              to="/bonos"
              className="hidden lg:flex items-center gap-2 bg-halo-brown text-halo-cream px-4 xl:px-6 py-2 xl:py-2.5 rounded-full font-medium text-xs xl:text-sm hover:bg-halo-charcoal transition-colors"
              data-testid="nav-reservar-btn"
            >
              Comprar bono
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-halo-cream/10 transition-colors text-halo-cream"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden bg-halo-charcoal border-t border-halo-cream/10 overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="px-4 py-4 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block px-4 py-3 rounded-xl font-medium text-sm transition-colors ${
                      location.pathname === link.path
                        ? 'bg-halo-cream/10 text-halo-cream'
                        : 'text-halo-cream/80 hover:bg-halo-cream/5 hover:text-halo-cream'
                    }`}
                    data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-3">
                  <Link
                    to="/bonos"
                    className="block w-full text-center bg-halo-brown text-halo-cream px-6 py-3.5 rounded-full font-medium text-sm hover:bg-halo-cream hover:text-halo-charcoal transition-colors"
                    data-testid="mobile-reservar-btn"
                  >
                    Comprar bono
                  </Link>
                </div>
                <div className="pt-4 pb-2 text-xs text-halo-cream/60 space-y-2 border-t border-halo-cream/10 mt-3">
                  <a href="tel:678901086" className="flex items-center gap-2 py-1 hover:text-halo-cream">
                    <Phone size={14} />
                    678 901 086
                  </a>
                  <span className="flex items-center gap-2">
                    <MapPin size={14} />
                    C/ Miquel Caplloch 6, Local 12
                  </span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navigation;

